import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/Button';
import { Link } from 'react-router-dom';
import { FileCheck, Plane, Users, CheckCircle } from 'lucide-react';
export function ServicesPage() {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="pt-20" data-id="element-529">
      {/* Hero */}
      <div className="bg-[#1e3a5f] text-white py-20" data-id="element-530">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-531">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-532">Our Services</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto" data-id="element-533">
            Comprehensive support for every step of your study abroad journey.
          </p>
        </div>
      </div>

      {/* Service 1: Admission Processing */}
      <section className="py-20 bg-white" data-id="element-534">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-535">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-536">
            <div data-id="element-537">
              <div className="inline-flex items-center justify-center p-3 bg-blue-100 rounded-lg mb-6" data-id="element-538">
                <FileCheck className="h-8 w-8 text-blue-600" data-id="element-539" />
              </div>
              <h2 className="text-3xl font-bold text-[#1e3a5f] mb-6" data-id="element-540">
                Admission Processing
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed" data-id="element-541">
                Navigating university applications can be complex. We simplify
                the process by handling everything from course selection to
                document submission, ensuring your application stands out.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-542">
                {['University & Course Selection', 'Statement of Purpose (SOP) Assistance', 'Application Review & Submission', 'Scholarship Application Support', 'Interview Preparation'].map(item => <li key={item} className="flex items-center text-gray-700" data-id="element-543">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3" data-id="element-544" />
                    {item}
                  </li>)}
              </ul>
              <Link to="/apply" data-id="element-545">
                <Button variant="primary" data-id="element-546">Start Application</Button>
              </Link>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl" data-id="element-547">
              <img src="https://images.unsplash.com/photo-1434030216411-0b793f4b4173?ixlib=rb-4.0.3&auto=format&fit=crop&w=1740&q=80" alt="Admission Processing" className="w-full h-full object-cover" data-id="element-548" />
            </div>
          </div>
        </div>
      </section>

      {/* Service 2: Visa Guidance */}
      <section className="py-20 bg-[#faf8f5]" data-id="element-549">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-550">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center lg:flex-row-reverse" data-id="element-551">
            <div className="lg:order-2" data-id="element-552">
              <div className="inline-flex items-center justify-center p-3 bg-green-100 rounded-lg mb-6" data-id="element-553">
                <Plane className="h-8 w-8 text-green-600" data-id="element-554" />
              </div>
              <h2 className="text-3xl font-bold text-[#1e3a5f] mb-6" data-id="element-555">
                Visa Guidance
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed" data-id="element-556">
                Securing a student visa is a critical step. Our experts guide
                you through the latest visa requirements and help you prepare a
                foolproof application.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-557">
                {['Document Verification', 'Financial Proof Guidance', 'Visa Application Filling', 'Mock Visa Interviews', 'Pre-departure Briefing'].map(item => <li key={item} className="flex items-center text-gray-700" data-id="element-558">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3" data-id="element-559" />
                    {item}
                  </li>)}
              </ul>
              <Link to="/contact" data-id="element-560">
                <Button variant="primary" data-id="element-561">Get Visa Help</Button>
              </Link>
            </div>
            <div className="lg:order-1 rounded-2xl overflow-hidden shadow-xl" data-id="element-562">
              <img src="https://images.unsplash.com/photo-1436491865332-7a61a109db05?ixlib=rb-4.0.3&auto=format&fit=crop&w=1740&q=80" alt="Visa Guidance" className="w-full h-full object-cover" data-id="element-563" />
            </div>
          </div>
        </div>
      </section>

      {/* Service 3: Career Counseling */}
      <section className="py-20 bg-white" data-id="element-564">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-565">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-566">
            <div data-id="element-567">
              <div className="inline-flex items-center justify-center p-3 bg-amber-100 rounded-lg mb-6" data-id="element-568">
                <Users className="h-8 w-8 text-amber-600" data-id="element-569" />
              </div>
              <h2 className="text-3xl font-bold text-[#1e3a5f] mb-6" data-id="element-570">
                Career Counseling
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed" data-id="element-571">
                We don't just help you get into university; we help you plan
                your future. Our counselors align your education choices with
                your long-term career goals.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-572">
                {['Psychometric Testing', 'Career Path Planning', 'Course ROI Analysis', 'Post-Study Work Options', 'Industry Trends Insights'].map(item => <li key={item} className="flex items-center text-gray-700" data-id="element-573">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3" data-id="element-574" />
                    {item}
                  </li>)}
              </ul>
              <Link to="/contact" data-id="element-575">
                <Button variant="primary" data-id="element-576">Book Session</Button>
              </Link>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl" data-id="element-577">
              <img src="https://images.unsplash.com/photo-1573497620053-ea5300f94f21?ixlib=rb-4.0.3&auto=format&fit=crop&w=1740&q=80" alt="Career Counseling" className="w-full h-full object-cover" data-id="element-578" />
            </div>
          </div>
        </div>
      </section>
    </motion.div>;
}